/**
 * Folder Tree Component
 *
 * Hierarchical tree view of folders with expand/collapse functionality.
 * Displays folder metadata and allows selection for edit/delete operations.
 */

import React from 'react';
import { useFolderStore, type FolderNode } from '../stores/folderStore';
import type { Folder } from '../api/folders';

interface FolderTreeProps {
  onSelectFolder?: (folder: Folder) => void;
  selectable?: boolean;
  showActions?: boolean;
}

export const FolderTree: React.FC<FolderTreeProps> = ({
  onSelectFolder,
  selectable = true,
  showActions = true,
}) => {
  const {
    folderTree,
    selectedFolder,
    isLoading,
    toggleExpanded,
    expandAll,
    collapseAll,
    openEditDialog,
    openDeleteDialog,
  } = useFolderStore();

  const renderFolderNode = (node: FolderNode, index: number) => {
    const { folder, children, depth, expanded } = node;
    const isSelected = selectedFolder?.id === folder.id;
    const hasChildren = children.length > 0;

    const indentStyle = {
      paddingLeft: `${depth * 20}px`,
    };

    const handleClick = () => {
      if (selectable && onSelectFolder) {
        onSelectFolder(folder);
      }
    };

    const handleToggleExpand = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (hasChildren) {
        toggleExpanded(folder.id);
      }
    };

    const handleEdit = (e: React.MouseEvent) => {
      e.stopPropagation();
      openEditDialog(folder);
    };

    const handleDelete = (e: React.MouseEvent) => {
      e.stopPropagation();
      openDeleteDialog(folder);
    };

    const folderName = folder.folder_path.split('/').filter(Boolean).pop() || '/';

    return (
      <div key={folder.id}>
        <div
          className={`
            flex items-center gap-2 py-2 px-3 cursor-pointer
            hover:bg-bridge-hover transition-colors
            ${isSelected ? 'bg-accent-blue/10 border-l-2 border-accent-orange' : ''}
          `}
          style={indentStyle}
          onClick={handleClick}
        >
          {/* Expand/Collapse Icon */}
          <button
            onClick={handleToggleExpand}
            className={`
              w-5 h-5 flex items-center justify-center
              ${hasChildren ? 'visible' : 'invisible'}
            `}
          >
            {hasChildren && (
              <span className="text-dark-text-muted">
                {expanded ? '▼' : '▶'}
              </span>
            )}
          </button>

          {/* Folder Icon */}
          <span className="text-accent-orange">
            {expanded ? '📂' : '📁'}
          </span>

          {/* Folder Name */}
          <span className="flex-1 font-medium text-white">{folderName}</span>

          {/* Denomination Badge */}
          {folder.denomination_id && (
            <span className="px-2 py-1 text-xs bg-accent-blue/20 text-accent-blue border border-accent-blue/30 rounded">
              {folder.denomination_id}
            </span>
          )}

          {/* Official Badge */}
          {folder.is_official && (
            <span className="px-2 py-1 text-xs bg-green-500/20 text-green-400 border border-green-500/30 rounded">
              Official
            </span>
          )}

          {/* Action Buttons */}
          {showActions && (
            <div className="flex gap-1">
              <button
                onClick={handleEdit}
                className="p-1.5 bg-blue-500/20 hover:bg-blue-500/30 border border-blue-500/30 text-blue-300 rounded transition-colors"
                title="Edit folder"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </button>
              <button
                onClick={handleDelete}
                className="p-1.5 bg-red-500/20 hover:bg-red-500/30 border border-red-500/30 text-red-300 rounded transition-colors"
                title="Delete folder"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            </div>
          )}
        </div>

        {/* Render Children */}
        {expanded && hasChildren && (
          <div>
            {children.map((child, idx) => renderFolderNode(child, idx))}
          </div>
        )}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-dark-text-muted">Loading folders...</div>
      </div>
    );
  }

  if (folderTree.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center p-8 text-dark-text-muted">
        <svg className="w-20 h-20 mb-4 opacity-40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
        </svg>
        <p className="text-lg font-semibold text-white mb-2">No folders yet</p>
        <p className="text-sm">Create your first folder to get started</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Tree Controls */}
      <div className="flex gap-2 p-3 border-b border-dark-border">
        <button
          onClick={expandAll}
          className="px-3 py-1 text-sm text-accent-blue hover:bg-accent-blue/10 rounded transition-colors"
        >
          Expand All
        </button>
        <button
          onClick={collapseAll}
          className="px-3 py-1 text-sm text-accent-blue hover:bg-accent-blue/10 rounded transition-colors"
        >
          Collapse All
        </button>
      </div>

      {/* Folder Tree */}
      <div className="flex-1 overflow-y-auto">
        {folderTree.map((node, index) => renderFolderNode(node, index))}
      </div>
    </div>
  );
};

export default FolderTree;
